import {
  FusionApplication,
  FusionApplicationBase,
  FusionComponent,
  FusionComponentApi,
  FusionDevHubTemplate,
  FusionEntity,
  FusionGitHubOrgBase,
  FusionHttpLink,
  FusionUser,
} from './catalog';
import {
  ANNOTATION_LOCATION,
  ANNOTATION_ORIGIN_LOCATION,
  ANNOTATION_SOURCE_LOCATION,
  ApiEntity,
  ComponentEntity,
  Entity,
  GroupEntity,
  parseEntityRef,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  stringifyEntityRef,
  SystemEntity,
  UserEntity,
} from '@backstage/catalog-model';
import {
  CATCH_ALL_GROUP_NAME,
  decodeBase64GZip,
  getOrgForScmSlug,
  isValidEntityRef,
  normalizeEntityRef,
  orgNameToNamespace,
} from '../../util';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { JsonObject } from '@backstage/types';
import { LRUCache } from '../cache';

/**
 * A cache of application dependencies. This is used to store the dependencies between applications and can
 * be recalled later to display the dependencies in the dependency diagram.
 */
const applicationDeps = new LRUCache<string, string[]>({
  maxSize: 1000,
  ttl: { seconds: 300 },
});

/**
 * Gets the application dependencies for a pair of entities
 * @param fromEntity The first entity. It is dependent on the second entity
 * @param toEntity The second entity. It is depended on by the first entity.
 */
export const getApplicationDeps = (fromEntity: string, toEntity: string) =>
  applicationDeps.get(`${fromEntity}->${toEntity}`);

interface TemplateEntityStepV1beta3 extends JsonObject {
  id?: string;
  name?: string;
  action: string;
  input?: JsonObject;
  if?: string | boolean;
}

interface TemplateParametersV1beta3 extends JsonObject {}

/**
 * Maps a Fusion component to a Backstage component entity
 * @param component The Fusion component
 */
export function fusionComponentToEntity(
  component: FusionComponent,
): ComponentEntity {
  const result: ComponentEntity = fusionComponentToFinalEntity(component);
  // Remove fields not used in non-final entities
  delete result.relations;
  delete result.metadata.uid;
  result.spec = {
    ...result.spec,
    consumesApis: (component.apisConsumedPaged?.componentApis ?? []).map(
      x => `api:default/${x.id}`,
    ),
    providesApis: (component.apisProvidedPaged?.componentApis ?? []).map(
      x => `api:default/${x.id}`,
    ),
    dependsOn: (component.dependsOnPaged?.components ?? []).map(
      x => `component:default/${x.id}`,
    ),
    system: (component.applicationsPaged?.applications ?? []).map(
      x => `system:default/${x.applicationId}`,
    )?.[0],
  };
  return result;
}

/**
 * Maps a Fusion component to a Backstage component entity.
 * Final entities are entities as they are stored in the catalog as opposed to entities as they are fetched from the source.
 *
 * @param component The Fusion component
 */
export function fusionComponentToFinalEntity(
  component: FusionComponent,
): ComponentEntity & { relations: Entity['relations'] } {
  return {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'Component',
    metadata: {
      namespace: 'default',
      name: component.id,
      title: component.title ?? component.name,
      description: component.description ?? undefined,
      links: processLinks(component.links),
      annotations: createAnnotations(component),
      tags: formatTags(component.tags),
      uid: component.id,
    },
    spec: {
      type: component.type ?? 'component',
      lifecycle: component.lifecycle,
      owner: getOwnerForEntity(component) ?? 'comcast',
    },
    relations: [
      ...(component?.apisProvidedPaged?.componentApis ?? []).map(api => ({
        type: RELATION_PROVIDES_API,
        targetRef: `api:default/${api.id}`,
      })),
      ...(component?.apisConsumedPaged?.componentApis ?? []).map(api => ({
        type: RELATION_CONSUMES_API,
        targetRef: `api:default/${api.id}`,
      })),
      ...(component?.applicationsPaged?.applications ?? []).map(app => ({
        type: RELATION_PART_OF,
        targetRef: `system:default/${app.applicationId}`,
      })),
      ...(component?.dependsOnPaged?.components ?? []).map(comp => ({
        type: RELATION_DEPENDS_ON,
        targetRef: `component:default/${comp.id}`,
      })),
      ...(component?.dependencyOfPaged?.components ?? []).map(comp => ({
        type: RELATION_DEPENDENCY_OF,
        targetRef: `component:default/${comp.id}`,
      })),
      {
        type: RELATION_OWNED_BY,
        targetRef: getOwnerForEntity(component) ?? 'comcast',
      },
    ],
  };
}

/**
 * Maps a Fusion GitHub organization to a Backstage group entity
 * @param params The parameters
 * @param params.orgName The name of the organization
 * @param params.group The Fusion GitHub organization
 * @param params.members The members of the organization
 */
export function fusionOrgToGroupEntity(
  params:
    | {
        orgName?: never;
        group: FusionGitHubOrgBase;
        members: { metadata: { namespace: string; name: string } }[];
      }
    | {
        orgName: string;
        group: FusionGitHubOrgBase;
        members: { metadata: { namespace: string; name: string } }[];
      },
): GroupEntity {
  return {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'Group',
    metadata: {
      namespace: orgNameToNamespace(params.orgName ?? params.group.name),
      name: params.orgName ? params.group.name : CATCH_ALL_GROUP_NAME,
      description: params.group.name,
      annotations: {
        [ANNOTATION_LOCATION]: 'fusion:groups',
        [ANNOTATION_ORIGIN_LOCATION]: 'fusion:groups',
      },
      uid: params.group.id,
    },
    spec: {
      type: 'group',
      members: (params.members ?? ([] as string[])).map(
        member => `user:${member.metadata.namespace}/${member.metadata.name}`,
      ),
      children: [],
    },
  };
}

/**
 * Maps a Fusion user to a Backstage API entity
 * @param user The Fusion user
 */
export function fusionUserToEntity(user: FusionUser): UserEntity {
  return {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'User',

    metadata: {
      namespace: 'default',
      name: user.login.toLowerCase(),
      description: user.title ?? user.login,
      annotations: {
        'github.com/user-login': user.login,
        [ANNOTATION_LOCATION]: 'fusion:users',
        [ANNOTATION_ORIGIN_LOCATION]: 'fusion:users',
      },
      uid: user.userId,
    },
    spec: {
      profile: {
        displayName: user.displayName,
        email: user.email ?? 'unspecified',
      },
      memberOf: (user.memberOf?.organization ?? []).map(
        org => `group:${orgNameToNamespace(org.name)}/${CATCH_ALL_GROUP_NAME}`,
      ),
    },
  };
}

/**
 * Maps a Fusion API to a Backstage API entity
 *
 * @param api The Fusion API
 */
export function fusionApiToEntity(api: FusionComponentApi): ApiEntity {
  const result: ApiEntity = fusionApiToFinalEntity(api);
  // Remove fields not used in non-final entities
  delete result.relations;
  delete result.metadata.uid;
  result.spec = {
    ...result.spec,
    system: (api.applicationsPaged?.applications ?? []).map(
      x => `system:default/${x.applicationId}`,
    )?.[0],
  };
  return result;
}

/**
 * Maps a Fusion Template to a Backstage Template entity
 *
 * @param template The Fusion Template
 */
export function fusionTemplateToEntity(
  template: FusionDevHubTemplate,
): TemplateEntityV1beta3 {
  return {
    apiVersion: 'scaffolder.backstage.io/v1beta3',
    kind: 'Template',
    metadata: {
      namespace: 'default',
      name: template.id,
      title: template.title ?? template.name,
      description: template.description ?? undefined,
      annotations: createAnnotations(template),
      links: processLinks(template.links),
      tags: formatTags(template.tags),
      uid: template.id,
    },
    spec: {
      type: template.type ?? 'template',
      owner: getOwnerForEntity(template) ?? 'comcast',
      parameters: template.parameters
        ? (JSON.parse(
            decodeBase64GZip(template.parameters) ?? '{}',
          ) as TemplateParametersV1beta3)
        : undefined,
      steps: template.steps
        ? (JSON.parse(
            decodeBase64GZip(template.steps) ?? '{}',
          ) as TemplateEntityStepV1beta3[])
        : [],
      output: template.output
        ? JSON.parse(decodeBase64GZip(template.output) ?? '{}')
        : undefined,
    },
  };
}

/**
 * Maps a Fusion API to a Backstage API entity
 *
 * Final entities are entities as they are stored in the catalog as opposed to entities as they are fetched from the source.
 * @param api The Fusion API
 */
export function fusionApiToFinalEntity(
  api: FusionComponentApi,
): ApiEntity & { relations: Entity['relations'] } {
  return {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'API',
    metadata: {
      namespace: 'default',
      name: api.id,
      title: api.title ?? api.name,
      description: api.description ?? undefined,
      annotations: createAnnotations(api),
      links: processLinks(api.links),
      tags: formatTags(api.tags),
      uid: api.id,
    },
    spec: {
      type: api.type ?? 'OpenAPI',
      lifecycle: api.lifecycle,
      owner: getOwnerForEntity(api) ?? 'comcast',
      definition:
        decodeBase64GZip(api.definitionPacked) ??
        `openapi: 3.0.0\ninfo:\n  title: ${
          api.title ?? api.name
        }\n  version: 0.0.0\n  description: No API Specification Provided!\n paths: {}\n`,
    },
    relations: [
      ...(api?.providedByComponentsPaged?.components ?? []).map(component => ({
        type: RELATION_API_PROVIDED_BY,
        targetRef: `component:default/${component.id}`,
      })),
      ...(api?.consumedByComponentsPaged?.components ?? []).map(component => ({
        type: RELATION_API_CONSUMED_BY,
        targetRef: `component:default/${component.id}`,
      })),
      ...(api?.applicationsPaged?.applications ?? []).map(app => ({
        type: RELATION_PART_OF,
        targetRef: `system:default/${app.applicationId}`,
      })),
      {
        type: RELATION_OWNED_BY,
        targetRef: getOwnerForEntity(api) ?? 'comcast',
      },
    ],
  };
}

/**
 * Maps an application to a Backstage system entity
 *
 * Final entities are entities as they are stored in the catalog as opposed to entities as they are fetched from the source.
 * @param application The application
 */
export function applicationToFinalEntity(
  application: FusionApplication | FusionApplicationBase,
): SystemEntity | (SystemEntity & { relations: Entity['relations'] }) {
  const { applicationId } = application;
  const entity = {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'System',
    metadata: {
      namespace: 'default',
      name: applicationId.toString(),
      title: application?.name ?? 'Application',
      annotations: {
        'comcast.net/itrc-id': applicationId.toString(),
        [ANNOTATION_LOCATION]: 'fusion:applications',
        [ANNOTATION_ORIGIN_LOCATION]: 'fusion:applications',
      },
      tags: ['application'],
      uid: applicationId.toString(),
    },
    spec: {
      owner: 'comcast',
    },
  } as SystemEntity;

  const fullApplication = application as FusionApplication;
  if (
    fullApplication?.componentsPaged?.components ||
    fullApplication?.componentApisPaged?.componentApis
  ) {
    entity.relations = [
      ...(entity.relations ?? []),
      ...(fullApplication?.componentsPaged?.components ?? []).map(
        component => ({
          type: RELATION_HAS_PART,
          targetRef: `component:default/${component.id}`,
          target: parseEntityRef(`component:default/${component.id}`),
        }),
      ),
      ...(fullApplication?.componentApisPaged?.componentApis ?? []).map(
        api => ({
          type: RELATION_HAS_PART,
          targetRef: `api:default/${api.id}`,
          target: parseEntityRef(`api:default/${api.id}`),
        }),
      ),
    ];
  }

  if (
    fullApplication?.dependsOnPaged?.dependencies ||
    fullApplication?.dependencyOfPaged?.dependencies
  ) {
    entity.relations = [
      ...(entity.relations ?? []),
      ...(fullApplication?.dependencyOfPaged?.dependencies ?? []).map(
        system => {
          applicationDeps.set(
            `system:default/${
              system.application.applicationId
            }->${stringifyEntityRef(entity)}`,
            system.sources.map(source => source.name),
          );
          return {
            type: RELATION_DEPENDENCY_OF,
            targetRef: `system:default/${system.application.applicationId}`,
            target: parseEntityRef(
              `system:default/${system.application.applicationId}`,
            ),
          };
        },
      ),
      ...(fullApplication?.dependsOnPaged?.dependencies ?? []).map(system => {
        applicationDeps.set(
          `${stringifyEntityRef(entity)}->system:default/${
            system.application.applicationId
          }`,
          system.sources.map(source => source.name),
        );
        return {
          type: RELATION_DEPENDS_ON,
          targetRef: `system:default/${system.application.applicationId}`,
          target: parseEntityRef(
            `system:default/${system.application.applicationId}`,
          ),
        };
      }),
    ];
  }

  return entity;
}

/**
 * Creates annotations for a component
 * @param component The component
 */
function createAnnotations(component: FusionEntity) {
  const annotations = {
    [ANNOTATION_SOURCE_LOCATION]: `url:${component.sourceLocationUrl}/`,
    [ANNOTATION_LOCATION]: 'fusion:entity',
    [ANNOTATION_ORIGIN_LOCATION]: 'fusion:entity',
    'ci.comcast.com/concourse-team': getOrgForScmSlug(component.scmSlug),
    'ci.comcast.com/concourse-pipeline': getRepoForEntity(component),
    'github.com/project-slug': getSlugForEntity(component),
    ...(component.annotations
      ? Object.fromEntries(
          component.annotations
            .filter(x => !x.startsWith('github.com'))
            .map(x => x.split(': ').map(part => part?.trim())),
        )
      : {}),
  };
  if (component.techDocsRef) {
    annotations['backstage.io/techdocs-ref'] = component.techDocsRef;
  }
  return annotations;
}

/**
 * Gets the owner for an entity
 * @param entity The entity
 */
export function getOwnerForEntity(entity: {
  scmSlug?: string;
  devHubOwner?: string;
}): string | undefined {
  const defaultOwner = `group:${orgNameToNamespace(
    getOrgForScmSlug(entity.scmSlug) ?? 'devx-discover',
  )}/${CATCH_ALL_GROUP_NAME}`;
  if (
    entity.devHubOwner &&
    isValidEntityRef(entity.devHubOwner, 'group', 'user')
  ) {
    return normalizeEntityRef(entity.devHubOwner, 'group') ?? defaultOwner;
  }
  return defaultOwner;
}

/**
 * Gets the repository for an entity
 * @param entity The entity
 */
function getRepoForEntity(entity: FusionEntity): string | undefined {
  return (entity?.scmSlug?.split('/') ?? [])?.[1];
}

/**
 * Gets the slug for an entity
 * @param entity The entity
 */
function getSlugForEntity(entity: FusionEntity): string | undefined {
  return (entity?.scmSlug?.split('/') ?? []).slice(0, 2).join('/') ?? '';
}

function formatTags(tags: string[] | undefined) {
  if (!tags) {
    return [];
  }
  return tags.map(tag =>
    tag
      .toLowerCase()
      .replaceAll(/[^a-z0-9+#]/gi, '-')
      .replaceAll(/-+/g, '-')
      .slice(0, 63),
  );
}

function isValidUrl(url: string) {
  // Check if the URL is valid
  try {
    const parsedUrl = new URL(url);
    return !(
      parsedUrl.protocol !== 'http:' &&
      parsedUrl.protocol !== 'https:' &&
      parsedUrl.protocol !== 'mailto:'
    );
  } catch {
    return false;
  }
}

function processLinks(links: FusionHttpLink[] | undefined): FusionHttpLink[] {
  if (links === undefined) {
    return [];
  }

  return (
    links
      ?.filter(x => isValidUrl(x.url))
      .map(link => ({
        url: link.url ?? undefined,
        title: link.title ?? undefined,
        icon: link.icon ?? undefined,
        type: link.type ?? undefined,
      })) ?? []
  );
}
